import { BACKLOG } from '@atlassian/jira-software-resource-invalidator/src/common/types.tsx';
import createResourcesCacheMiddleware from '@atlassian/jira-software-resource-invalidator/src/controllers/create-resources-cache-middleware/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	LOAD_APP_DATA_REQUEST,
	LOAD_CONFIG_DATA_REQUEST,
	SET_CONFIG_DATA,
	SET_APP_DATA,
	INITIALIZE_APP_PROPS,
	BACKLOG_VIEW,
	REFRESH_DATA_REQUEST,
	LOAD_CONFIG_DATA_MODEL_SUCCESS,
} from '../../actions/app/index.tsx';
import {
	CARD_LIST_TOGGLE,
	CARD_LISTS_DISABLE,
	CARD_LISTS_ENABLE_ALL,
	SET_COLLAPSED_CARD_LIST,
} from '../../actions/card-list/index.tsx';
import { FILTERED_CARD_CLEAR } from '../../actions/cards/filtered-cards/index.tsx';
import { CARD_FOCUS, CARD_BLUR } from '../../actions/cards/focus/index.tsx';
import { CARD_PREV, CARD_NEXT } from '../../actions/cards/navigate/index.tsx';
import { CARD_REFRESH_REQUEST } from '../../actions/cards/refresh/index.tsx';
import {
	SET_CARD_ID_WITH_ABOVE_ICC_OPEN,
	SET_RICH_ACTIVE_CARD,
} from '../../actions/cards/rich-card/index.tsx';
import {
	CARD_CLICK,
	CARD_EMPTY_SELECTION,
	CARD_MULTI_SELECT,
	CARD_SINGLE_SELECT,
	CARD_TOGGLE_SELECTION,
	PARENT_CLICK,
	SET_LAST_SELECTED_CARD_KEY,
} from '../../actions/cards/selection/index.tsx';
import { SET_COLUMNS } from '../../actions/columns/index.tsx';
import {
	CLOSE_DETAIL_VIEW,
	OPEN_DETAIL_VIEW,
	OPEN_DETAIL_VIEW_DEBOUNCED,
	UPDATE_DETAIL_VIEW,
} from '../../actions/detail-view/index.tsx';
import { DRAG_END, DRAG_START } from '../../actions/drag/index.tsx';
import { SET_ALL_FILTERS } from '../../actions/filters/index.tsx';
import { OPEN_MODAL, CLOSE_MODAL } from '../../actions/modals/index.tsx';
import {
	CLOSE_INSIGHTS_PANEL,
	SET_ALL_PANELS,
	SET_ALL_PANELS_SUCCESS,
} from '../../actions/panels/index.tsx';
import { LOAD_PRIORITIES_REQUEST } from '../../actions/priorities/index.tsx';
import { REALTIME_DISPATCH_EVENT_BATCH } from '../../actions/realtime/dispatch/index.tsx';
import { REQUEST_CURRENT_USER, SET_CURRENT_USER } from '../../actions/user/index.tsx';
import { SET_VERSIONS } from '../../actions/version/index.tsx';
import {
	SET_CONFLUENCE_DATA,
	SET_USER_PROPERTY_KEYS,
} from '../../actions/whiteboards-entrypoint-experiment/index.tsx';
import { SET_INLINE_CREATE_SESSION_ID } from '../../actions/inline-create/index.tsx';

// We ignore the success and failure mutations, we just invalidate them as soon as the request
// is fired. That way if someone navigates away before the SUCCESS/FAILURE mutation returns they
// won't get an incorrect cached version
const BACKLOG_IGNORE_REGEX = /^(state\.actions\.).+(_SUCCESS|_FAILURE)$/;

// This one helps us ignore junk like COMMON/REDUX_DISPOSABLE/DISPOSE_ACTION
const ELIGIBLE_FOR_INVALIDATION = /^(state\.actions\.).+$/;

export const shouldTriggerCacheInvalidation = ({ type }: { type: string }) => {
	switch (type) {
		case REFRESH_DATA_REQUEST: // If we get a refresh call it was most likely from a real-time update
		case LOAD_APP_DATA_REQUEST: // fired on startup
		case LOAD_CONFIG_DATA_REQUEST: // fired on startup
		case LOAD_PRIORITIES_REQUEST:
		case SET_CONFIG_DATA: // fired on startup
		case LOAD_CONFIG_DATA_MODEL_SUCCESS: // config data isn't cached yet
		case SET_APP_DATA: // fired on startup
		case REQUEST_CURRENT_USER: // Fired on startup
		case SET_CURRENT_USER: // Fired on startup
		case INITIALIZE_APP_PROPS: // startup
		case BACKLOG_VIEW: // startup
		case SET_ALL_FILTERS: // doesn't impact the state since the filters reset on each view
		case SET_COLUMNS:
		case CARD_PREV: // these don't persist between transitions
		case CARD_NEXT: // these don't persist between transitions
		case CARD_FOCUS: // these don't persist between transitions
		case CARD_BLUR: // these don't persist between transitions
		case SET_RICH_ACTIVE_CARD: // hover
		case CARD_CLICK: // click a card
		case CARD_SINGLE_SELECT: // click a card
		case SET_LAST_SELECTED_CARD_KEY: // Query string selectedIssue
		case CARD_LIST_TOGGLE: // toggling a card list
		case SET_COLLAPSED_CARD_LIST:
		case OPEN_DETAIL_VIEW: // open bento
		case CLOSE_DETAIL_VIEW: // close bento
		case OPEN_MODAL: // We don't care about opening a modal. We only care about what they do in the modal
		case CLOSE_MODAL: // Prime example is Edit modal but CANCEL
		case REALTIME_DISPATCH_EVENT_BATCH: // Don't invalidate on the request, invalidate on success/failure
		case SET_CARD_ID_WITH_ABOVE_ICC_OPEN: // UI
		case CARD_REFRESH_REQUEST: // refresh doesn't update the data
		case DRAG_START: // separate actions for card, sprint mutations
		case DRAG_END: // separate actions for card, sprint mutations
		case UPDATE_DETAIL_VIEW: // UI to open Bento
		case CARD_EMPTY_SELECTION: // UI
		case PARENT_CLICK: // UI
		case CARD_LISTS_ENABLE_ALL: // UI
		case CARD_TOGGLE_SELECTION: // UI
		case FILTERED_CARD_CLEAR: // UI
		case CARD_LISTS_DISABLE: // UI
		case OPEN_DETAIL_VIEW_DEBOUNCED: // UI
		case CARD_MULTI_SELECT: // UI
		case CLOSE_INSIGHTS_PANEL:
		case SET_VERSIONS:
		case SET_ALL_PANELS:
		case SET_ALL_PANELS_SUCCESS:
			return false;
		default:
			break;
	}

	if (fg('fix_backlog_board_transition_cache')) {
		switch (type) {
			case SET_CONFLUENCE_DATA: // fired on startup
			case SET_INLINE_CREATE_SESSION_ID: // fired on startup
			case SET_USER_PROPERTY_KEYS: // fired on startup
				return false;
			default:
				break;
		}
	}

	// Ignore stuff that isn't applicable to the board (e.g. COMMON/REDUX_DISPOSABLE/DISPOSE_ACTION)
	// Test against board specific actions
	if (!ELIGIBLE_FOR_INVALIDATION.exec(type) || BACKLOG_IGNORE_REGEX.exec(type)) {
		return false;
	}

	// DEFAULT TO TRUE. It's better to forcibly refresh the cache if someone forgets to add
	// their mutation here
	return true;
};

export default createResourcesCacheMiddleware(BACKLOG, shouldTriggerCacheInvalidation);
